/**
 * Sidebar v2 slim row: the one-line row for the snoozed and settled shelves.
 *
 * Ported from pingdotgg/t3code `apps/web/src/components/Sidebar.tsx`
 * (SidebarThreadRow slim variant, ~lines 1583-1664) at pin
 * e3e7cc3fc2e02966efe4efc45216dadcd046bfbb (MIT; see
 * THIRD-PARTY-NOTICES.md). Adapted: subscriptions and derived values come
 * from sidebarV2RowModel over forge's shared row modules.
 */
import {
  AlarmClockIcon,
  AlarmClockOffIcon,
  CheckIcon,
  PinIcon,
  TerminalIcon,
  Undo2Icon,
} from "lucide-react";

import { cn } from "~/lib/utils";
import { Tooltip, TooltipPopup, TooltipTrigger } from "~/components/ui/tooltip";
import { synchronizeTerminalPulse } from "~/components/ThreadStatusIndicators";
import { SidebarThreadPullRequestBadge } from "~/components/sidebar/SidebarThreadPullRequestBadge";
import { JumpHintBadge, settledTimeLabel, threadTimeLabel } from "./sidebarV2Chrome";
import { DROP_VERB_LABEL, useSortableRootProps } from "./sidebarV2RowParts";
import type { SidebarV2RowProps } from "./sidebarV2RowParts";
import { useSidebarV2RowModel, type SidebarV2RowModel } from "./sidebarV2RowModel";
import { SidebarV2Tooltip } from "./sidebarV2Tooltip";
import {
  SidebarV2OriginIcon,
  SidebarV2ProjectIcon,
  SidebarV2ProviderIcon,
} from "./SidebarV2IdentityMeta";

/** The slim row's live terminal-state glyph. */
function SlimTerminal(props: { readonly model: SidebarV2RowModel; readonly threadId: string }) {
  if (props.model.terminalStatus === null) return null;
  return (
    <span
      role="img"
      aria-label={`${props.model.terminalProcessCount} terminal ${props.model.terminalProcessCount === 1 ? "process" : "processes"} running`}
      data-testid={`sidebar-terminal-status-${props.threadId}`}
      className={cn(
        "inline-flex shrink-0 items-center justify-center",
        props.model.terminalStatus.colorClass,
      )}
    >
      <TerminalIcon
        className={cn(
          "size-3.5",
          props.model.terminalStatus.pulse && "motion-safe:animate-status-pulse",
        )}
        onAnimationStart={synchronizeTerminalPulse}
      />
    </span>
  );
}

/** The slim row's leading badges: pin, terminal state, and the PR badge. */
function SlimBadges(props: {
  readonly model: SidebarV2RowModel;
  readonly threadId: string;
  readonly isPinned: boolean;
  readonly linkedPrUrl: string | null;
  readonly onOpenPullRequest: (event: React.MouseEvent<HTMLElement>, url: string) => void;
}) {
  const { model } = props;
  const pinIndicator = props.isPinned ? (
    <PinIcon aria-label="Pinned" role="img" className="size-3 shrink-0 text-muted-foreground/65" />
  ) : null;
  const prBadge =
    model.pr !== null || props.linkedPrUrl !== null ? (
      <SidebarThreadPullRequestBadge
        badge={{
          number: model.pr?.number ?? null,
          url: model.pr?.url ?? props.linkedPrUrl ?? null,
        }}
        status={model.linkedPrIndicator}
        onOpenPullRequest={props.onOpenPullRequest}
      />
    ) : null;
  return (
    <>
      {pinIndicator}
      <SlimTerminal model={model} threadId={props.threadId} />
      {prBadge}
    </>
  );
}

/** The slim title span: prominence follows the row's attention state. */
function SlimTitle(props: {
  readonly model: SidebarV2RowModel;
  readonly threadTitle: string;
  readonly isActive: boolean;
}) {
  const { model } = props;
  return (
    <span
      className={cn(
        "min-w-0 flex-1 truncate",
        model.shouldRecede
          ? "text-secondary-label/70"
          : props.isActive || model.isWoke || model.status === "input"
            ? "text-foreground"
            : "text-secondary-label/70",
      )}
    >
      {props.threadTitle}
    </span>
  );
}

/** The slim row's right slot: the wake/time label plus the section action. */
function SlimRightSlot(props: {
  readonly model: SidebarV2RowModel;
  readonly thread: import("~/types").SidebarThreadSummary;
  readonly variantAction: "settle" | "unsettle" | "unsnooze";
  readonly snoozeWakeLabelText: string | null;
  readonly snoozeSupported: boolean;
}) {
  const { model, thread, variantAction } = props;
  const wakeButton =
    variantAction === "unsnooze" && props.snoozeSupported ? (
      <button
        type="button"
        aria-label="Wake thread now"
        onClick={model.unsnooze}
        className="pointer-events-none absolute inset-y-0 right-0 -mr-1 inline-flex cursor-pointer items-center gap-1 rounded-md bg-transparent px-1.5 text-xs text-muted-foreground opacity-0 transition-opacity hover:text-foreground focus-visible:pointer-events-auto focus-visible:opacity-100 group-hover/sidebar-row:pointer-events-auto group-hover/sidebar-row:opacity-100"
      >
        <AlarmClockOffIcon className="mb-px size-3" />
      </button>
    ) : null;

  if (variantAction === "unsnooze") {
    return (
      <span className="relative ml-auto flex h-6 min-w-8 shrink-0 items-center justify-end">
        {props.snoozeWakeLabelText !== null ? (
          <span className="text-xs text-info-foreground tabular-nums">
            {props.snoozeWakeLabelText}
          </span>
        ) : null}
        {wakeButton}
      </span>
    );
  }
  if (model.isWoke) {
    return (
      <span className="relative ml-auto flex h-6 min-w-8 shrink-0 items-center justify-end">
        <Tooltip>
          <TooltipTrigger
            render={
              <button
                type="button"
                aria-label="Dismiss Woke notification"
                onClick={model.handleAcknowledgeWokeClick}
                className="inline-flex cursor-pointer items-center gap-1 rounded-sm text-xs font-medium text-warning-foreground outline-none hover:underline focus-visible:ring-2 focus-visible:ring-ring"
              >
                <AlarmClockIcon aria-hidden className="size-3" />
                <span role="status">Woke</span>
              </button>
            }
          />
          <TooltipPopup side="top">Dismiss Woke notification</TooltipPopup>
        </Tooltip>
      </span>
    );
  }
  return (
    <span className="relative ml-auto flex h-6 min-w-8 shrink-0 items-center justify-end">
      <span className="text-xs">
        {variantAction === "unsettle" ? settledTimeLabel(thread) : threadTimeLabel(thread)}
      </span>
      {variantAction === "unsettle" ? (
        <Tooltip>
          <TooltipTrigger
            render={
              <button
                type="button"
                aria-label="Un-settle thread"
                onClick={model.unsettle}
                className="pointer-events-none absolute inset-y-0 right-0 -mr-1 inline-flex cursor-pointer items-center gap-1 rounded-md bg-transparent px-1.5 text-xs text-muted-foreground opacity-0 transition-opacity hover:text-foreground focus-visible:pointer-events-auto focus-visible:opacity-100 group-hover/sidebar-row:pointer-events-auto group-hover/sidebar-row:opacity-100"
              />
            }
          >
            <Undo2Icon className="mb-px size-3.5" />
          </TooltipTrigger>
          <TooltipPopup side="top">Un-settle thread</TooltipPopup>
        </Tooltip>
      ) : (
        <button
          type="button"
          aria-label="Settle thread"
          onClick={model.settle}
          className="pointer-events-none absolute inset-y-0 right-0 inline-flex cursor-pointer items-center gap-1 rounded-md bg-transparent px-2 text-xs text-muted-foreground opacity-0 transition-opacity hover:text-foreground focus-visible:pointer-events-auto focus-visible:opacity-100 group-hover/sidebar-row:pointer-events-auto group-hover/sidebar-row:opacity-100"
        >
          <CheckIcon className="size-3" />
        </button>
      )}
    </span>
  );
}

/** The snoozed and settled slim row. */
export function SidebarV2RowSlim(props: SidebarV2RowProps) {
  const model = useSidebarV2RowModel(props);
  const { thread, variantAction } = props;
  const sortable = props.sortable;
  const sortableRootProps = useSortableRootProps(sortable);
  const dragging = sortable?.isDragging === true;

  return (
    <li
      data-thread-item
      {...sortableRootProps}
      className={cn(
        "list-none [content-visibility:auto] [contain-intrinsic-size:auto_36px]",
        dragging && "relative z-20",
      )}
    >
      <Tooltip disabled={dragging}>
        <TooltipTrigger
          render={
            <div
              ref={model.rowRef}
              role="button"
              tabIndex={0}
              data-testid="sidebar-row-slim"
              className={cn(
                "group/sidebar-row relative flex h-9 w-full cursor-pointer items-center gap-2.5 overflow-hidden rounded-md px-2.5 text-left text-sidebar-foreground text-sm outline-none select-none hover:bg-sidebar-row-hover",
                props.isActive && "bg-sidebar-row-active",
                model.isSelected && "bg-sidebar-row-selected",
                model.shouldRecede && "text-sidebar-muted-foreground/75",
              )}
              onClick={model.handleClick}
              onDoubleClick={model.handleDoubleClick}
              onKeyDown={model.handleKeyDown}
              onContextMenu={model.handleContextMenu}
            />
          }
        >
          <SidebarV2ProjectIcon
            environmentId={thread.environmentId}
            cwd={props.projectCwd}
            name={props.projectDisplayName}
            canonicalIdentity={props.projectCanonicalIdentity}
            className="size-4 shrink-0"
          />
          <SlimTitle model={model} threadTitle={thread.title} isActive={props.isActive} />
          <SidebarV2OriginIcon origin={props.threadOrigin ?? "current-device"} />
          <SidebarV2ProviderIcon provider={thread.modelSelection?.provider} className="size-3.5" />
          <SlimBadges
            model={model}
            threadId={thread.id}
            isPinned={props.isPinned}
            linkedPrUrl={props.linkedPrUrl}
            onOpenPullRequest={props.onOpenPullRequest}
          />
          {dragging && props.dropVerb !== null ? (
            <span
              role="status"
              className="pointer-events-none ml-auto inline-flex h-5 shrink-0 items-center gap-1 rounded-sm border border-primary/40 bg-primary/10 px-1.5 text-2xs font-medium text-primary"
            >
              {DROP_VERB_LABEL[props.dropVerb] ?? props.dropVerb}
            </span>
          ) : (
            <SlimRightSlot
              model={model}
              thread={thread}
              variantAction={variantAction}
              snoozeWakeLabelText={props.snoozeWakeLabelText}
              snoozeSupported
            />
          )}
          {props.jumpLabel ? <JumpHintBadge label={props.jumpLabel} /> : null}
        </TooltipTrigger>
        <SidebarV2Tooltip
          thread={thread}
          projectDisplayName={props.projectDisplayName}
          environmentLabel={props.environmentLabel}
          environmentMachine={props.environmentMachine}
          modelLabel={thread.session?.provider ?? thread.modelSelection?.model ?? null}
          terminalStatus={model.terminalStatus}
          terminalProcessCount={model.terminalProcessCount}
          pullRequestEntries={model.prEntries}
          projectCwd={props.projectCwd}
          projectCanonicalIdentity={props.projectCanonicalIdentity}
          displayBranch={model.displayBranch}
          threadOrigin={props.threadOrigin ?? "current-device"}
        />
      </Tooltip>
    </li>
  );
}
